import React, { useState, useEffect } from 'react';
import { 
  Users, Plus, Edit, Trash2, UserCheck, UserX, Mail, 
  Shield, User, Crown, X, Save
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import api from '../../utils/api';
import { toast } from 'sonner';

const ClientTeam = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: 'member'
  });

  const roles = [
    { value: 'admin', label: 'Admin', description: 'Full access to all features', icon: Shield },
    { value: 'member', label: 'Member', description: 'Standard access', icon: User }
  ];

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      const response = await api.get('/client-team');
      setTeamMembers(response.data);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
      // Silently fail if endpoint doesn't exist yet
      if (error.response?.status !== 404) {
        toast.error('Failed to load team members');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      first_name: '',
      last_name: '',
      role: 'member'
    });
  };

  const handleInvite = () => {
    resetForm();
    setEditingMember(null);
    setShowInviteModal(true);
  };

  const handleSave = async () => {
    if (!formData.email || !formData.first_name || !formData.last_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSaving(true);
    try {
      if (editingMember) {
        await api.put(`/client-team/${editingMember.id}`, {
          role: formData.role
        });
        toast.success('Team member updated');
      } else {
        await api.post('/client-team', formData);
        toast.success('Team member invited successfully');
      }
      
      setShowInviteModal(false);
      resetForm();
      setEditingMember(null);
      fetchTeamMembers();
    } catch (error) {
      console.error('Failed to save team member:', error);
      toast.error(error.response?.data?.detail || 'Failed to save team member');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (member) => {
    setFormData({
      email: member.user?.email || '',
      first_name: member.user?.first_name || '',
      last_name: member.user?.last_name || '',
      role: member.role
    });
    setEditingMember(member);
    setShowInviteModal(true);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    
    try {
      await api.delete(`/client-team/${deleteConfirm.id}`);
      toast.success('Team member removed');
      setDeleteConfirm(null);
      fetchTeamMembers();
    } catch (error) {
      console.error('Failed to remove team member:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove team member');
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner': return Crown;
      case 'admin': return Shield;
      default: return User;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'owner': return 'bg-yellow-100 text-yellow-700';
      case 'admin': return 'bg-purple-100 text-purple-700';
      default: return 'bg-blue-100 text-blue-700';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <UserCheck className="w-3 h-3 mr-1" />
            Active
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
            <Mail className="w-3 h-3 mr-1" />
            Pending
          </span>
        );
      case 'inactive':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
            <UserX className="w-3 h-3 mr-1" />
            Inactive
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900" data-testid="team-title">Your Team</h2>
          <p className="text-gray-600">Manage users who have access to your account</p>
        </div>
        
        <Button 
          onClick={handleInvite}
          className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white"
          data-testid="invite-member-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Team Member
        </Button>
      </div>

      {/* Team Members List */}
      <div className="space-y-4">
        {teamMembers.length === 0 ? (
          <div className="glass rounded-xl p-12 text-center">
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No team members yet</h3>
            <p className="text-gray-500 mb-4">
              Invite colleagues to collaborate on your account
            </p>
            <Button onClick={handleInvite} variant="outline">
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Team Member
            </Button>
          </div>
        ) : (
          <div className="glass rounded-xl overflow-hidden">
            <div className="divide-y divide-gray-200">
              {teamMembers.map((member, index) => {
                const RoleIcon = getRoleIcon(member.role);
                
                return (
                  <div
                    key={member.id}
                    className="p-4 hover:bg-gray-50 transition-colors"
                    data-testid={`team-member-${index}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center text-white font-bold">
                          {member.user?.first_name?.[0]}{member.user?.last_name?.[0]}
                        </div>
                        
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-gray-900">
                              {member.user?.first_name} {member.user?.last_name}
                            </h4>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getRoleColor(member.role)}`}>
                              <RoleIcon className="w-3 h-3 mr-1" />
                              {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                            </span>
                            {getStatusBadge(member.status)}
                          </div>
                          <p className="text-sm text-gray-500">{member.user?.email}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            Added {new Date(member.invited_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(member)}
                          className="text-gray-600 hover:text-teal-600"
                          data-testid={`edit-member-${index}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteConfirm(member)}
                          className="text-gray-600 hover:text-red-600"
                          data-testid={`remove-member-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Invite/Edit Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingMember ? 'Edit Team Member' : 'Add Team Member'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {!editingMember && (
              <>
                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    data-testid="member-email-input"
                    type="email"
                    placeholder="colleague@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name">First Name *</Label>
                    <Input
                      id="first_name"
                      data-testid="member-firstname-input"
                      placeholder="John"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Last Name *</Label>
                    <Input
                      id="last_name"
                      data-testid="member-lastname-input"
                      placeholder="Doe"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    />
                  </div>
                </div>
              </>
            )}

            <div>
              <Label>Role *</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                {roles.map((role) => {
                  const Icon = role.icon;
                  return (
                    <div
                      key={role.value}
                      onClick={() => setFormData({ ...formData, role: role.value })}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.role === role.value
                          ? 'border-teal-500 bg-teal-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`role-${role.value}`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 ${formData.role === role.value ? 'text-teal-600' : 'text-gray-500'}`} />
                        <span className="font-medium text-gray-900">{role.label}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{role.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {editingMember && (
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-600">
                  <strong>Member:</strong> {editingMember.user?.first_name} {editingMember.user?.last_name}
                </p>
                <p className="text-sm text-gray-500">{editingMember.user?.email}</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowInviteModal(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white"
              data-testid="save-member-btn"
            >
              {saving ? (
                <>Saving...</>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {editingMember ? 'Update' : 'Add Member'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Remove Team Member?
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-gray-600">
              Are you sure you want to remove <strong>{deleteConfirm?.user?.first_name} {deleteConfirm?.user?.last_name}</strong> from your team? 
              They will lose access to your account.
            </p>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-remove-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientTeam;
